import { useState, useCallback } from "react";

export interface BulkOperationItem {
    /** Human-readable label shown in the progress modal (e.g. "Austin Best") */
    label: string;
    /** The async operation to run for this item */
    fn: () => Promise<void>;
}

export interface BulkOperationError {
    label: string;
    message: string;
}

export interface BulkOperationState {
    isRunning: boolean;
    completed: number;
    total: number;
    currentLabel: string;
    errors: BulkOperationError[];
    isDone: boolean;
}

const initialState: BulkOperationState = {
    isRunning: false,
    completed: 0,
    total: 0,
    currentLabel: "",
    errors: [],
    isDone: false,
};

export function useBulkOperation() {
    const [state, setState] = useState<BulkOperationState>(initialState);

    const run = useCallback(async (operations: BulkOperationItem[]) => {
        if (operations.length === 0) return;

        setState({
            isRunning: true,
            completed: 0,
            total: operations.length,
            currentLabel: operations[0].label,
            errors: [],
            isDone: false,
        });

        const errors: BulkOperationError[] = [];

        for (let i = 0; i < operations.length; i++) {
            const op = operations[i];
            setState(prev => ({ ...prev, currentLabel: op.label, completed: i }));

            try {
                await op.fn();
            } catch (err) {
                const message = err instanceof Error ? err.message : "Unknown error";
                errors.push({ label: op.label, message });
            }
        }

        setState({
            isRunning: false,
            completed: operations.length,
            total: operations.length,
            currentLabel: "",
            errors,
            isDone: true,
        });
    }, []);

    const reset = useCallback(() => {
        setState(initialState);
    }, []);

    return { state, run, reset };
}
